import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Button,
  ThemeProvider,
  Container,
  Stack,
} from '@mui/material';
import dayjs from 'dayjs';
import {useEffect, useState} from 'react';
import {useQueryClient} from '@tanstack/react-query';
import {
  companyIdSelector,
  isGTSelector,
  isNRSelector,
} from '../../../../store/Slices/companySlice';
import {useStoreSelector} from '../../../../store/configstore';
import {
  IGetBaseMessageRequestResponace,
  queryKeyMessageRequestList,
  useRqViewBaseListMessagesByRequestID,
} from '../../../../react_query/messages/MessagesByCmpId';
import MusicPreviewBtn from '../../../../components/music/MusicPreviewBtn';
import {RequestTheme} from '../../../../components/layouts/Theme';
import DialogApiRequestStatus from '../../../../components/dialogs/DialogApiRequestStatus';
import CommonDialog from '../../../../components/CommonDialog';
import { apiPatch } from '../../../../axiosConfig';
import { useSelector } from 'react-redux';
import { useUserCanAny } from '../../../../hooks/useUserCan';
import { useSharedMessageState } from '../../../../hooks/useSharedMessageState';
import { ReadOnlyMessageBanner } from '../../../messages/sharing/ReadOnlyMessageBanner';
import DistrictShareAction from '../../../messages/sharing/DistrictShareAction';
import {useDistrictMessageSharingAccess} from '../../../messages/sharing/useDistrictMessageSharingAccess';

interface SharedPreviewContext {
  isSharedReadOnly?: boolean;
  ownerCompanyName?: string;
  visibilityOrigin?: string;
}

const ViewBaseMsgList = ({
  id,
  sharedPreviewContext,
}: {
  id: number;
  sharedPreviewContext?: SharedPreviewContext;
}) => {
  const queryClient = useQueryClient();
  const companyId = useStoreSelector(companyIdSelector);
  const isGT = useSelector(isGTSelector);
  const isNR = useSelector(isNRSelector);
  const isAdminAndViewStaff = useUserCanAny('is-admin,base-message:view');
  const canUpdateBaseMessage = useUserCanAny(
    isNR
      ? 'is-admin,partner,base-message:update'
      : 'is-admin,base-message:update',
  );
  const {canManageDistrictSharing} = useDistrictMessageSharingAccess(companyId);

  const [content, setContent] = useState<IGetBaseMessageRequestResponace>();
  const [voiceArtist, setVoiceArtist] = useState<string>('');
  const [apiState, setApiState] = useState({status: '', error: null});
  const [open, setOpen] = useState(false);
  const [hasExpireDate, setHasExpireDate] = useState<boolean>(false);

  const {data, isFetched, isError, isLoading} =
    useRqViewBaseListMessagesByRequestID(companyId, id, Boolean(id));

  const {isReadOnly, isShared, ownerCompanyName, visibilityOrigin} =
    useSharedMessageState(data?.shared_metadata);
  const effectiveIsReadOnly = Boolean(
    sharedPreviewContext?.isSharedReadOnly || isReadOnly,
  );
  const effectiveIsShared = Boolean(
    sharedPreviewContext?.isSharedReadOnly || isShared,
  );
  const effectiveOwnerCompanyName =
    sharedPreviewContext?.ownerCompanyName || ownerCompanyName;
  const effectiveVisibilityOrigin =
    sharedPreviewContext?.visibilityOrigin || visibilityOrigin;
  const canShowStatusAction = Boolean(
    content && content.status !== 'In Progress' && canUpdateBaseMessage,
  );
  const canShowActionsRow =
    !effectiveIsReadOnly && (canManageDistrictSharing || canShowStatusAction);

  useEffect(() => {
    if (isFetched && !isError && data) {
      setContent(data);
      setVoiceArtist(data?.message?.voice_artist?.name || '');
      setHasExpireDate(Boolean(data?.expire_date));
    }
  }, [data, isError, isFetched]);
  const handleStatus = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setApiState({
      status: '',
      error: null,
    });
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleEdit = () => {
    setApiState({
      status: '',
      error: null,
    });
  };

  const handleSubmit = () => {
    const id = content?.id;
    setApiState({
      status: 'pending',
      error: null,
    });
    const payload = {
      status: content?.status === 'Active' ? '0' : '1', //If status already in active we have to switch it's state
    };
    apiPatch(`/v1/company/${companyId}/message/base-message/${id}`, payload)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: [queryKeyMessageRequestList(companyId, 'base-message')],
          refetchType: 'active',
        });
        setApiState({
          status: 'success',
          error: null,
        });
      })
      .catch(error => {
        setApiState({
          status: 'error',
          error,
        });
      });
  };

  if (isLoading || (!content && !isError)) {
    return <Typography>Loading message preview...</Typography>;
  }

  if (isError) {
    return <Typography>Unable to load message preview.</Typography>;
  }

  return (
    <>
      <ThemeProvider theme={RequestTheme}>
        <Container className='p-0' tabIndex={0}>
          {effectiveIsShared && effectiveOwnerCompanyName && (
            <ReadOnlyMessageBanner
              ownerCompanyName={effectiveOwnerCompanyName}
              visibilityOrigin={effectiveVisibilityOrigin}
            />
          )}
          <TableContainer className='bg-transparent'>
            <Table>
              <TableBody>
                {voiceArtist && isNR && (
                  <TableRow>
                    <TableCell sx={{width: '200px'}}>Voice(s)</TableCell>
                    <TableCell>{voiceArtist}</TableCell>
                  </TableRow>
                )}
                {isGT && (
                  <TableRow>
                    <TableCell>School(s)</TableCell>
                    <TableCell>
                      {content?.schools?.map(val => val.name).join(', ')}
                    </TableCell>
                  </TableRow>
                )}
                <TableRow>
                  <TableCell>Station(s)</TableCell>
                  <TableCell>
                    {content?.stations
                      ?.map(val => val.label || val.name)
                      .join(', ')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Message Name</TableCell>
                  <TableCell>{content?.name}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Content</TableCell>
                  <TableCell>
                    <pre>{content?.message?.message}</pre>
                  </TableCell>
                </TableRow>
                {content?.events && content?.events?.length ? (
                  <TableRow>
                    <TableCell>Sports / Events</TableCell>
                    <TableCell>
                      {content?.events
                        .map(event => event.event_name)
                        .join(', ')}
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell>Season(s)</TableCell>
                    <TableCell>
                      {content?.is_all_seasons === 1
                        ? 'Year Around'
                        : content?.seasons
                            ?.map(season => season.season_name)
                            .join(', ')}
                    </TableCell>
                  </TableRow>
                )}
                {isAdminAndViewStaff && (
                  <>
                    <TableRow>
                      <TableCell>Voice Artist</TableCell>
                      <TableCell>
                        {content?.voice_artist_name || 'NA'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Message Creator</TableCell>
                      <TableCell>
                        {content?.message_creator_name || 'NA'}
                      </TableCell>
                    </TableRow>
                  </>
                )}
                <TableRow>
                  <TableCell>Created Date</TableCell>
                  <TableCell>
                    {dayjs(content?.created_at).format('MM/DD/YYYY hh:mm:ss A')}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Created By</TableCell>
                  <TableCell>{content?.created_by}</TableCell>
                </TableRow>
                {content?.message?.media_track ? (
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell>
                      {content?.message.media_track ? (
                        <Stack spacing={1} direction='row' alignItems='center'>
                          <MusicPreviewBtn
                            id={content?.message.media_track.id}
                            url={content?.message.media_track.url}
                            track_name={
                              content?.message.media_track?.track_name ||
                              content?.name
                            }
                            track_artist={
                              content?.message.media_track?.track_artist
                            }
                            isDownloadable
                            downloadFileName={
                              content?.message.media_track?.track_name
                            }
                          />
                          <Typography>
                            {content?.message.media_track?.track_name}
                          </Typography>
                        </Stack>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ) : null}
                {hasExpireDate ? (
                  <TableRow>
                    <TableCell>Expire Date</TableCell>
                    <TableCell>{content?.expire_date}</TableCell>
                  </TableRow>
                ) : null}
                {content && canShowActionsRow ? (
                  <TableRow>
                    <TableCell>Actions</TableCell>
                    <TableCell>
                      <Stack direction='row' spacing={2} flexWrap='wrap'>
                        {canManageDistrictSharing ? (
                          <DistrictShareAction
                            companyId={companyId}
                            messageType='base'
                            size='medium'
                            requestId={content.id}
                            existingTargets={
                              content.shared_metadata?.share_targets ?? []
                            }
                          />
                        ) : null}
                        {canShowStatusAction ? (
                          <Button
                            onClick={handleStatus}
                            variant='outlined'
                            color={
                              content.status === 'Inactive'
                                ? 'success'
                                : 'error'
                            }>
                            {content.status === 'Inactive'
                              ? 'Restore'
                              : 'Deactivate'}
                          </Button>
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {apiState.status !== '' && (
            <DialogApiRequestStatus
              apiState={apiState}
              onRetry={handleSubmit}
              onEdit={handleEdit}
              onClose={handleClose}
            />
          )}
          <CommonDialog
            open={open}
            status={content?.status === 'Inactive' ? false : true}
            handleSubmit={handleSubmit}
            handleCancel={handleCancel}
          />
        </Container>
      </ThemeProvider>
    </>
  );
};

export default ViewBaseMsgList;
